import { LiaTransparentElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { navigate } from '../../router.js';
import { loginOptions, pollLogin, providerStartUrl, setDeveloper, startLogin } from '../api/developer.js';

/**
 * Signing in as a developer.
 *
 * <b>A developer and an operator do not sign in with the same thing, and pretending otherwise would
 * mean giving every developer a portal account.</b> A developer signs in with an identity provider and
 * is handed a client session - the same credential their CLI and editor extension hold. An operator has
 * an account in this service's own directory and signs in with a user name and a password, on
 * `<app-login>`. Which one an address renders is decided in `user/routes.js`.
 *
 * ## Providers first
 *
 * A provider is the only way a person comes into existence here, so the providers are the first and
 * the primary way in. The buttons come from `login/options`, so a provider added on the server appears
 * here with no change: each one is a redirect to `login/{provider}/start`, the provider sends the
 * browser back to the service, and the service lands it on `/login/exchange` with a single-use code
 * that page spends for the session. A deployment with no provider configured says plainly that nobody
 * new can sign in until one is.
 *
 * ## The link, for a returning person
 *
 * A link by email signs in somebody who has signed in before, and never creates a person - which is why
 * the form is labelled for them. The link may be opened anywhere - on a phone, in another browser, by a
 * mail client that fetches it in a sandbox. So the tab that asked for it does not wait for a redirect:
 * it holds a device code and asks whether the flow has completed, exactly as the CLI does.
 */
export class UserLogin extends LiaTransparentElement
{
   static properties = {
      next: { type: String },
      state: { state: true },
      email: { state: true },
      error: { state: true },
      busy: { state: true },
      options: { state: true }
   };

   constructor()
   {
      super();
      this.next = '/';
      this.state = 'asking';
      this.email = '';
      this.error = '';
      this.busy = false;
      this.timer = null;
      this.deviceCode = '';

      // Shown once the link is sent, and typed where the link opens. It is what proves the person at
      // the link is the person at this page, so a link somebody else asked for cannot sign them in here.
      this.userCode = '';

      // What this deployment offers, asked once. Until it answers the page waits, so the provider
      // buttons do not arrive under a form that was drawn first.
      this.options = null;

      /*
       * **Ask again the moment the tab is looked at.**
       *
       * The link is routinely opened somewhere else - another tab, a mail client, a phone - which leaves
       * this tab in the background, and a background tab's timers are clamped by the browser. So somebody
       * who opens the link and comes straight back finds a page that has not asked recently and looks
       * stuck, and reloading it appears to be what signs them in. Nothing was broken; the wait was.
       */
      this.onVisible = () =>
      {
         if (document.visibilityState === 'visible' && this.state === 'sent' && this.deviceCode)
         {
            this.poll(this.deviceCode, 0);
         }
      };
   }

   connectedCallback()
   {
      super.connectedCallback();
      document.addEventListener('visibilitychange', this.onVisible);

      // Where a provider sent the browser back with nothing: the callback lands here and says why.
      this.error = REFUSALS[new URLSearchParams(location.search).get('error')] ?? '';

      loginOptions()
         .then(options => { this.options = options; })
         .catch(() => { this.options = { providers: [], magicLink: true }; });
   }

   disconnectedCallback()
   {
      super.disconnectedCallback();
      document.removeEventListener('visibilitychange', this.onVisible);
      this.stopPolling();
   }

   stopPolling()
   {
      if (this.timer)
      {
         clearTimeout(this.timer);
         this.timer = null;
      }
   }

   async ask(event)
   {
      event.preventDefault();

      const address = String(new FormData(event.target).get('email') ?? '').trim();

      if (!address)
      {
         return;
      }

      this.error = '';
      this.busy = true;

      try
      {
         const started = await startLogin(address);

         this.email = address;
         this.state = 'sent';
         this.deviceCode = started.deviceCode;
         this.userCode = started.userCode ?? '';
         this.poll(started.deviceCode);
      }
      catch (error)
      {
         this.error = error.message;
      }
      finally
      {
         this.busy = false;
      }
   }

   /** Leaves for the provider. A page load rather than a fetch: the provider's page is where this goes. */
   signInWith(provider)
   {
      this.busy = true;
      location.assign(providerStartUrl(provider.id, { next: this.next }));
   }

   /**
    * Asks again until the link is opened.
    *
    * `setTimeout` chained rather than `setInterval`, so a slow answer cannot overlap the next request -
    * and so the wait is measured from when the last one *finished*.
    */
   poll(deviceCode, delay = POLL_MS)
   {
      this.stopPolling();

      this.timer = setTimeout(async () =>
      {
         try
         {
            const status = await pollLogin(deviceCode);

            if (status.state === 'complete' && status.session)
            {
               setDeveloper({
                  token: status.session,
                  principalId: status.principalId,
                  email: status.email ?? this.email,
                  expiresAt: status.expiresAt
               });

               navigate(this.next, { replace: true });
               return;
            }

            if (status.state === 'denied')
            {
               this.state = 'asking';
               this.error = status.message || 'That sign-in was refused. Start again.';
               return;
            }

            if (status.state === 'expired')
            {
               this.state = 'expired';
               return;
            }
         }
         catch
         {
            // A failed poll is not a failed sign-in - the link may still be unopened and the network may
            // still come back. Keep asking; the flow expires on its own if nothing happens.
         }

         this.poll(deviceCode);
      }, delay);
   }

   render()
   {
      return html`
         <lia-auth-layout
            .showThemeToggle=${false}
            brand="covdbg"
            brand-icon="fa-solid fa-bug"
            heading="Sign in"
            description="Your seats, your repositories and the decisions covdbg made for you."
            error=${this.error}
            max-width="460"
         >
            ${this.state === 'sent' ? this.sent : nothing}
            ${this.state === 'expired' ? this.expired : nothing}
            ${this.state === 'asking' ? this.form : nothing}
            <nav aria-label="Product and policies" class="d-flex flex-wrap justify-content-center gap-3 small mt-4">
               <a href="/pricing">Plans and billing</a>
               <a href="https://covdbg.com/imprint/terms/">Terms</a>
               <a href="https://covdbg.com/imprint/privacy/">Privacy</a>
               <a href="/pricing#refund-policy">Refunds</a>
            </nav>
         </lia-auth-layout>`;
   }

   get form()
   {
      if (!this.options)
      {
         return html`
            <p class="text-body-secondary small text-center mb-0">
               <lia-spinner size="sm" class="me-2"></lia-spinner>One moment
            </p>`;
      }

      const providers = this.options.providers ?? [];

      return html`
         ${providers.length
            ? html`
               ${providers.map(provider => html`
                  <button
                     class="btn btn-primary w-100 mb-2"
                     type="button"
                     ?disabled=${this.busy}
                     @click=${() => this.signInWith(provider)}
                  >
                     <i class=${`${PROVIDER_ICONS[provider.id] ?? 'fa-solid fa-right-to-bracket'} me-2`} aria-hidden="true"></i>
                     Sign in with ${provider.name}
                  </button>`)}
               <p class="form-text text-center mb-3">
                  The first time, and whenever you like. Your account there is what covdbg knows you by.
               </p>`
            : html`
               <lia-alert
                  variant="warning"
                  icon="fa-solid fa-triangle-exclamation"
                  message="Nobody new can sign in on this deployment until an identity provider is configured. Somebody who has signed in before can still get a link by email."
               ></lia-alert>`}
         <div class="d-flex align-items-center gap-2 mb-3 text-body-secondary small">
            <hr class="flex-grow-1 m-0"><span>Signed in before? Get a link by email</span><hr class="flex-grow-1 m-0">
         </div>
         <form @submit=${this.ask}>
            <div class="mb-3">
               <label class="form-label" for="developer-email">Email address</label>
               <input
                  id="developer-email"
                  class="form-control"
                  type="email"
                  name="email"
                  autocomplete="email"
                  placeholder="you@example.com"
                  .value=${this.email}
                  required
                  ?disabled=${this.busy}
               >
               <div class="form-text">
                  An address on your profile. A link signs you in; it does not create an account.
               </div>
            </div>
            <button class="btn btn-outline-secondary w-100" type="submit" ?disabled=${this.busy}>
               ${this.busy
                  ? html`<lia-spinner size="sm" class="me-2"></lia-spinner>Sending`
                  : 'Email me a link'}
            </button>
         </form>`;
   }

   get sent()
   {
      return html`
         <div class="text-center py-2">
            <i class="fa-solid fa-envelope-open-text fa-2x text-primary mb-3" aria-hidden="true"></i>
            <p class="mb-1">Check <strong>${this.email}</strong>.</p>
            <p class="text-body-secondary small mb-2">Open the link and enter this code there</p>
            <p class="display-6 font-monospace user-select-all mb-3" data-user-code>${this.userCode}</p>
            <p class="text-body-secondary small mb-3">
               Then this page signs you in - it keeps asking, so you can open the link anywhere, on any
               device. The code is what proves it was you who asked.
            </p>
            <p class="text-body-secondary small mb-0">
               <lia-spinner size="sm" class="me-2"></lia-spinner>Waiting for the link
            </p>
         </div>`;
   }

   get expired()
   {
      return html`
         <div class="text-center py-2">
            <p class="mb-3">That ran out before it was finished.</p>
            <button
               class="btn btn-outline-secondary"
               type="button"
               @click=${() => { this.state = 'asking'; this.error = ''; }}
            >Start again</button>
         </div>`;
   }
}

/** The icon a provider's button carries; anything unknown gets the generic one. */
export const PROVIDER_ICONS = {
   github: 'fa-brands fa-github'
};

/** What the provider callback says when it lands here with nothing, keyed by its `error`. */
const REFUSALS = {
   denied: 'The sign-in was refused at the provider. Nothing was signed in.',
   failed: 'The provider did not say who you are. Try again.',
   state: 'That sign-in took too long, or the link was changed on the way. Start again.'
};

/**
 * How often the tab asks.
 *
 * Three seconds: fast enough that opening the link feels like it signed this tab in, slow enough that a
 * page somebody left open overnight is not a request every second for eight hours. The flow expires
 * server-side regardless, and the poll stops with it.
 */
const POLL_MS = 3000;

customElements.define('user-login', UserLogin);
